import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Typography,
  Spin,
  message,
  Select,
  Row,
  Col,
  Statistic,
  Layout,
  Tag,
  Dropdown,
} from 'antd';
import {
  FileTextOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/plugin/isoWeek';
import 'dayjs/plugin/isSameOrBefore';
import 'dayjs/plugin/isSameOrAfter';
import apiService from '../../services/api';
import { Tooltip, Legend, ResponsiveContainer, CartesianGrid, XAxis, YAxis, BarChart, Bar } from 'recharts';
import DateRangeFilter from '../../components/DateRangeFilter';

dayjs.extend(require('dayjs/plugin/isoWeek'));
dayjs.extend(require('dayjs/plugin/isSameOrBefore'));
dayjs.extend(require('dayjs/plugin/isSameOrAfter'));

const { Title, Text } = Typography;
const { Content } = Layout;


const initialDateFilter = {
  year: dayjs().year(),
  month: dayjs().month(),
  // dateRange: undefined, // Only use year/month for initial state
};

const FinancialReport = () => {
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState(initialDateFilter);
  const [typeFilter, setTypeFilter] = useState('all');
  const [plPositionFilter, setPlPositionFilter] = useState('all');

  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalExpenses: 0,
    netIncome: 0,
    totalOutstandingIncome: 0,
    paidInvoicesCount: 0,
    overdueInvoicesCount: 0,
    averageIncomeTransaction: 0,
    averageExpenseTransaction: 0,
    paidExpenses: 0,
    paidIncome: 0,
    externalVanIncome: 0,
    externalVanExpense: 0,
    externalVanNetIncome: 0,
    expenseByCategory: [],
    totalIncomeVAT: 0,
    totalExpensesVAT: 0,
    expenseByCategoryVAT: [],
    externalVanIncomeVAT: 0,
    externalVanExpenseVAT: 0,
    paidIncomeVAT: 0,
    paidExpensesVAT: 0,
    vanIncomeByVan: {}
  });
  const [transactions, setTransactions] = useState([]);
  
  const [incomeExpenseByPeriod, setIncomeExpenseByPeriod] = useState([]);

  useEffect(() => {
    if (
      (dateFilter.month === 'custom' && dateFilter.dateRange && dateFilter.dateRange[0] && dateFilter.dateRange[1]) ||
      (dateFilter.month !== 'custom')
    ) {
      fetchFinancialData();
    }
    // eslint-disable-next-line
  }, [dateFilter.year, dateFilter.month, dateFilter.dateRange, typeFilter, plPositionFilter]);

  const fetchFinancialData = async () => {
    try {
      setLoading(true);
      // Build params based on dateFilter mode
      const params = {
        status: typeFilter,
        plPosition: plPositionFilter,
        format: 'json'
      };
      if (dateFilter.dateRange && dateFilter.dateRange[0] && dateFilter.dateRange[1]) {
        params.startDate = dateFilter.dateRange[0].format('YYYY-MM-DD');
        params.endDate = dateFilter.dateRange[1].format('YYYY-MM-DD');
      } else if (
        typeof dateFilter.year !== 'undefined' &&
        typeof dateFilter.month !== 'undefined'
      ) {
        params.year = dateFilter.year;
        params.month = dateFilter.month;
      }

      const response = await apiService.reports.getFinancialReport(params);
      const { data } = response.data;

      setTransactions(data.data || []);
      setIncomeExpenseByPeriod(data.periodData || []);

      // Update statistics
      const newStats = {
        totalRevenue: data.stats.totalRevenue || 0,
        totalExpenses: data.stats.totalExpenses || 0,
        netIncome: data.stats.netIncome || 0,
        totalOutstandingIncome: data.stats.totalOutstandingIncome || 0,
        paidInvoicesCount: data.stats.paidInvoicesCount || 0,
        overdueInvoicesCount: data.stats.overdueInvoicesCount || 0,
        averageIncomeTransaction: data.stats.averageIncomeTransaction || 0,
        averageExpenseTransaction: data.stats.averageExpenseTransaction || 0,
        paidExpenses: data.stats.paidExpenses || 0,
        paidIncome: data.stats.paidIncome || 0,
        externalVanIncome: data.stats.externalVanIncome || 0,
        externalVanExpense: data.stats.externalVanExpense || 0,
        externalVanNetIncome: data.stats.externalVanNetIncome || 0,
        expenseByCategory: data.stats.expenseByCategory || [],
        totalIncomeVAT: data.stats.totalIncomeVAT || 0,
        totalExpensesVAT: data.stats.totalExpensesVAT || 0,
        expenseByCategoryVAT: data.stats.expenseByCategoryVAT || [],
        externalVanIncomeVAT: data.stats.externalVanIncomeVAT || 0,
        externalVanExpenseVAT: data.stats.externalVanExpenseVAT || 0,
        paidIncomeVAT: data.stats.paidIncomeVAT || 0,
        paidExpensesVAT: data.stats.paidExpensesVAT || 0,
        vanIncomeByVan: data.stats.vanIncomeByVan || {}
      };
      setStats(newStats);

    } catch (error) {
      console.error('Error fetching financial data:', error);
      message.error('Failed to fetch financial data');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      setLoading(true);
      const params = {
        status: typeFilter,
        plPosition: plPositionFilter,
        format: format
      };

      if (dateFilter.dateRange && dateFilter.dateRange[0] && dateFilter.dateRange[1]) {
        params.startDate = dateFilter.dateRange[0].format('YYYY-MM-DD');
        params.endDate = dateFilter.dateRange[1].format('YYYY-MM-DD');
      } else if (
        typeof dateFilter.year !== 'undefined' &&
        typeof dateFilter.month !== 'undefined'
      ) {
        params.year = dateFilter.year;
        params.month = dateFilter.month;
      }

      const response = await apiService.reports.getFinancialReport(params);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `financial_report.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success(`Report exported successfully as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Error exporting report:', error);
      message.error('Failed to export report');
    } finally {
      setLoading(false);
    }
  };

  const exportMenuItems = [
    {
      key: 'csv',
      icon: <FileTextOutlined />,
      label: 'Export as CSV',
      onClick: () => handleExport('csv')
    },
    {
      key: 'excel',
      icon: <FileTextOutlined />,
      label: 'Export as Excel',
      onClick: () => handleExport('excel')
    }
  ];

  const columns = [
    {
      title: 'Payment Create Date',
      dataIndex: 'createdAt',
      key: 'paymentCreateDate',
      render: (date) => date ? dayjs(date).format('DD/MM/YYYY HH:mm') : 'N/A'
    },
    {
      title: 'Customer',
      dataIndex: 'job',
      key: 'customer',
      render: (job) => job?.customer?.name || 'N/A'
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => `£${amount?.toFixed(2) || '0.00'}`
    },
    {
      title: 'VAT',
      dataIndex: 'vatAmount',
      key: 'vatAmount',
      render: (vat) => `£${vat?.toFixed(2) || '0.00'}`
    },
    {
      title: 'Total Amount',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (total) => `£${total?.toFixed(2) || '0.00'}`
    },
    {
      title: 'Payment Status',
      dataIndex: 'status',
      key: 'paymentStatus',
      render: (status) => {
        if (!status) return <Tag>N/A</Tag>;
        const statusColors = {
          'pending': 'warning',
          'paid': 'success',
          'overdue': 'error',
          'cancelled': 'default'
        };
        const color = statusColors[status.toLowerCase()] || 'default';
        const displayStatus = status.charAt(0).toUpperCase() + status.slice(1);
        return <Tag color={color}>{displayStatus}</Tag>;
      }
    },
    {
      title: 'Payment Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (date) => date ? dayjs.utc(date).format('DD/MM/YYYY') : 'N/A'
    },
    {
      title: 'Job Status',
      dataIndex: 'job',
      key: 'jobStatus',
      render: (job) => {
        if (!job?.jobStatus) return <Tag>N/A</Tag>;
        const displayStatus = job.jobStatus.charAt(0).toUpperCase() + job.jobStatus.slice(1);
        return <Tag>{displayStatus}</Tag>;
      }
    },
    {
      title: 'External Reference (Job)',
      dataIndex: 'job',
      key: 'externalJobReference',
      render: (job) => job?.bookingReference || 'N/A'
    }
  ];

  const handleDateFilterChange = (newValue) => {
    setDateFilter(newValue);
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Layout>
      <Content style={{ padding: '24px' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Title level={2}>Financial Snapshot & Report</Title>
            <Text type="secondary">Analyze financial performance, income, and expenses.</Text>
          </div>



          {/* Filters Row */}
          <Card title="Filters" style={{ marginBottom: 16 }}>
            {/* Date Filter Row */}
            <Row gutter={16} align="middle" style={{ flexWrap: 'wrap' }}>
              <Col xs={24} sm={12} md={10} lg={8} xl={8}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text>Date Range:</Text>
                  <DateRangeFilter value={dateFilter} onChange={handleDateFilterChange} />
                </Space>
              </Col>
            </Row>
            {/* Other Filters Row */}
            <Row gutter={16} align="middle" style={{ flexWrap: 'wrap', marginTop: 8 }}>
              {/* P/L Position Filter */}
              <Col xs={24} sm={12} md={8} lg={6} xl={6}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text>P/L Position:</Text>
                  <Select value={plPositionFilter} onChange={setPlPositionFilter} style={{ width: '100%' }}>
                    <Select.Option value="all">All P/L</Select.Option>
                    <Select.Option value="income">Income</Select.Option>
                    <Select.Option value="expense">Expense</Select.Option>
                  </Select>
                </Space>
              </Col>
              {/* Payment Status Filter */}
              <Col xs={24} sm={12} md={8} lg={6} xl={6}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Text>Payment Status:</Text>
                  <Select value={typeFilter} onChange={setTypeFilter} style={{ width: '100%' }}>
                    <Select.Option value="all">All Statuses</Select.Option>
                    <Select.Option value="pending">Pending</Select.Option>
                    <Select.Option value="paid">Paid</Select.Option>
                    <Select.Option value="overdue">Overdue</Select.Option>
                    <Select.Option value="cancelled">Cancelled</Select.Option>
                  </Select>
                </Space>
              </Col>
            </Row>
          </Card>

          {loading && transactions.length === 0 && <div style={{textAlign: 'center', padding: '20px'}}><Spin tip="Initialising report..."/></div>} 
          {(!loading || transactions.length > 0) && (
            <>
              {/* Row 1: Income and Expenses Overview */}
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Card>
                    <Statistic
                      title="Total Revenue"
                      value={stats.totalRevenue}
                      precision={2}
                      prefix="£"
                      valueStyle={{ color: '#3f8600' }}
                    />
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                      VAT: £{(stats.totalIncomeVAT || 0).toFixed(2)}
                    </div>
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Card>
                    <Statistic
                      title="Total Expenses"
                      value={stats.totalExpenses}
                      precision={2}
                      prefix="£"
                      valueStyle={{ color: '#cf1322' }}
                    />
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                      VAT: £{(stats.totalExpensesVAT || 0).toFixed(2)}
                    </div>
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Card>
                    <Statistic
                      title="Net Income"
                      value={stats.netIncome}
                      precision={2}
                      prefix="£"
                      valueStyle={{ color: stats.netIncome >= 0 ? '#3f8600' : '#cf1322' }}
                    />
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                      VAT: £{((stats.totalIncomeVAT || 0) - (stats.totalExpensesVAT || 0)).toFixed(2)}
                    </div>
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Card>
                    <Statistic
                      title="Received income"
                      value={stats.paidIncome}
                      precision={2}
                      prefix="£"
                      valueStyle={{ color: '#3f8600' }}
                    />
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                      VAT: £{(stats.paidIncomeVAT || 0).toFixed(2)}
                    </div>
                  </Card>
                </Col>
              </Row>

              {/* Row 2: External Van and Invoice Statistics */}
              <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Card>
                    <Statistic
                      title="External Van Income"
                      value={stats.externalVanIncome}
                      precision={2}
                      prefix="£"
                      valueStyle={{ color: '#3f8600' }}
                    />
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                      VAT: £{(stats.externalVanIncomeVAT || 0).toFixed(2)}
                    </div>
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Card>
                    <Statistic
                      title="External Van Expenses"
                      value={stats.externalVanExpense}
                      precision={2}
                      prefix="£"
                      valueStyle={{ color: '#cf1322' }}
                    />
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                      VAT: £{(stats.externalVanExpenseVAT || 0).toFixed(2)}
                    </div>
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Card>
                    <Statistic
                      title="External Van Net Income"
                      value={stats.externalVanNetIncome}
                      precision={2}
                      prefix="£"
                      valueStyle={{ color: stats.externalVanNetIncome >= 0 ? '#3f8600' : '#cf1322' }}
                    />
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                      VAT: £{((stats.externalVanIncomeVAT || 0) - (stats.externalVanExpenseVAT || 0)).toFixed(2)}
                    </div>
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Card>
                    <Statistic
                      title="Paid Expenses"
                      value={stats.paidExpenses}
                      precision={2}
                      prefix="£"
                      valueStyle={{ color: '#cf1322' }}
                    />
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                      VAT: £{(stats.paidExpensesVAT || 0).toFixed(2)}
                    </div>
                  </Card>
                </Col>
              </Row>

              {/* Row 3: Expense Categories */}
              <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                {[
                  { category: 'Salary', key: 'salary' },
                  { category: 'Maintenance', key: 'maintenance' },
                  { category: 'Fuel', key: 'fuel' },
                  { category: 'Charges', key: 'charges' }
                ].map(({ category, key }) => {
                  const expenseData = (stats.expenseByCategory || []).find(
                    cat => cat.category.toLowerCase() === category.toLowerCase()
                  );
                  const vatObj = (stats.expenseByCategoryVAT || []).find(
                    v => v.category && v.category.toLowerCase() === category.toLowerCase()
                  );
                  return (
                    <Col xs={24} sm={12} md={8} lg={6} key={key}>
                      <Card>
                        <Statistic
                          title={`${category} Expenses`}
                          value={expenseData?.amount || 0}
                          precision={2}
                          prefix="£"
                          valueStyle={{ color: '#cf1322' }}
                        />
                        <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                          VAT: £{(vatObj?.vatAmount || 0).toFixed(2)}
                        </div>
                      </Card>
                    </Col>
                  );
                })}
              </Row>

              {/* Additional expense categories */}
              {(stats.expenseByCategory || [])
                .filter(cat => {
                  const category = cat.category.toLowerCase();
                  return !['job', 'salary', 'maintenance', 'fuel', 'charges'].includes(category);
                })
                .length > 0 && (
                <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                  {(stats.expenseByCategory || [])
                    .filter(cat => {
                      const category = cat.category.toLowerCase();
                      return !['job', 'salary', 'maintenance', 'fuel', 'charges'].includes(category);
                    })
                    .slice(0, 4)
                    .map(cat => {
                      const vatObj = (stats.expenseByCategoryVAT || []).find(
                        v => v.category && v.category.toLowerCase() === cat.category.toLowerCase()
                      );
                      return (
                        <Col xs={24} sm={12} md={8} lg={6} key={cat.category}>
                          <Card>
                            <Statistic
                              title={`${cat.category} Expenses`}
                              value={cat.amount}
                              precision={2}
                              prefix="£"
                              valueStyle={{ color: '#cf1322' }}
                            />
                            <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                              VAT: £{(vatObj?.vatAmount || 0).toFixed(2)}
                            </div>
                          </Card>
                        </Col>
                      );
                    })}
                  {/* Fill empty slots if less than 4 categories in this row */}
                  {Array.from({
                    length: 4 - (stats.expenseByCategory || [])
                      .filter(cat => {
                        const category = cat.category.toLowerCase();
                        return !['job', 'salary', 'maintenance', 'fuel', 'charges'].includes(category);
                      })
                      .slice(0, 4).length
                  }).map((_, idx) => (
                    <Col xs={24} sm={12} md={8} lg={6} key={`empty-col-extra-${idx}`}></Col>
                  ))}
                </Row>
              )}

              <Row gutter={[16, 16]} style={{marginTop: 16}}>
                <Col xs={24} lg={12}>
                  <Row gutter={[16,16]}>
                    <Col span={24}>
                        <Card title="Income vs Expense (Last 3 Months)">
                            <div style={{ height: 300 }}>
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                  data={(() => {
                                    let filtered = incomeExpenseByPeriod;
                                    // If filtering by a specific month, show that month plus previous 2 months
                                    if (dateFilter && typeof dateFilter.year === 'number' && typeof dateFilter.month === 'number' && dateFilter.month !== 'custom') {
                                      const selectedMonth = dayjs().year(dateFilter.year).month(dateFilter.month);
                                      const startMonth = selectedMonth.subtract(2, 'month');
                                      const endMonth = selectedMonth;
                                      
                                      filtered = incomeExpenseByPeriod.filter(item => {
                                        const itemDate = dayjs(item.period + '-01');
                                        return itemDate.isSameOrAfter(startMonth, 'month') && itemDate.isSameOrBefore(endMonth, 'month');
                                      });
                                    } else if (dateFilter && dateFilter.dateRange && dateFilter.dateRange.length === 2) {
                                      // If custom range, show only months in the range (up to 3, most recent first)
                                      const start = dateFilter.dateRange[0].startOf('month');
                                      const end = dateFilter.dateRange[1].endOf('month');
                                      filtered = incomeExpenseByPeriod.filter(item => {
                                        const d = dayjs(item.period + '-01');
                                        return d.isSameOrAfter(start) && d.isSameOrBefore(end);
                                      });
                                    }
                                    // Show at most 3, most recent first
                                    filtered = filtered.slice(-3);
                                    // Format period as 'MMM YYYY'
                                    return filtered.map(item => ({
                                      ...item,
                                      period: dayjs(item.period + '-01').format('MMM YYYY'),
                                    }));
                                  })()}
                                  margin={{ top: 20, right: 30, left: 0, bottom: 20 }}
                                >
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="period" />
                                  <YAxis tickFormatter={v => `£${v}`} />
                                  <Tooltip formatter={v => `£${Number(v).toFixed(2)}`} contentStyle={{ background: '#1f1f1f', border: '1px solid #333', color: '#fff' }} itemStyle={{ color: '#fff' }} labelStyle={{ color: '#fff', fontWeight: 600 }} cursor={false} />
                                  <Legend />
                                  <Bar dataKey="income" name="Income" fill="#00C49F" />
                                  <Bar dataKey="expense" name="Expense" fill="#FF8042" />
                                </BarChart>
                              </ResponsiveContainer>
                            </div>
                        </Card>
                    </Col>
                  </Row>
                </Col>
                <Col xs={24} lg={12}>
                  <Row gutter={[16,16]}>
                    <Col span={24}>
                        <Card title="Van Income (Last 3 Months)">
                            <div style={{ height: 300 }}>
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                  data={(() => {
                                    // Determine the 3-month period based on date filter
                                    let startMonth, endMonth;
                                    
                                    if (dateFilter && typeof dateFilter.year === 'number' && typeof dateFilter.month === 'number' && dateFilter.month !== 'custom') {
                                      // If filtering by a specific month, show that month plus previous 2 months
                                      const selectedMonth = dayjs().year(dateFilter.year).month(dateFilter.month);
                                      startMonth = selectedMonth.subtract(2, 'month');
                                      endMonth = selectedMonth;
                                    } else if (dateFilter && dateFilter.dateRange && dateFilter.dateRange.length === 2) {
                                      // If custom range, use the range but limit to 3 months
                                      const start = dateFilter.dateRange[0].startOf('month');
                                      const end = dateFilter.dateRange[1].endOf('month');
                                      const monthsDiff = end.diff(start, 'month') + 1;
                                      
                                      if (monthsDiff <= 3) {
                                        startMonth = start;
                                        endMonth = end;
                                      } else {
                                        // If more than 3 months, take the last 3 months from the end
                                        endMonth = end;
                                        startMonth = end.subtract(2, 'month');
                                      }
                                    } else {
                                      // Default: last 3 months from current date
                                      endMonth = dayjs();
                                      startMonth = dayjs().subtract(2, 'month');
                                    }
                                    
                                    // Generate the 3 months for the chart
                                    const month1 = startMonth.format('YYYY-MM');
                                    const month2 = startMonth.add(1, 'month').format('YYYY-MM');
                                    const month3 = endMonth.format('YYYY-MM');
                                    
                                    // Convert van income data to chart format
                                    return Object.entries(stats.vanIncomeByVan || {})
                                      .filter(([van, data]) => van !== 'Unknown') // Filter out Unknown van
                                      .map(([van, data]) => ({
                                        van: van === 'EXTERNAL_VAN' ? 'External Van' : van,
                                        [month1]: data.months?.[month1] || 0,
                                        [month2]: data.months?.[month2] || 0,
                                        [month3]: data.months?.[month3] || 0,
                                        total: data.total || 0
                                      }))
                                      .sort((a, b) => b.total - a.total);
                                  })()}
                                  margin={{ top: 20, right: 30, left: 0, bottom: 20 }}
                                >
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis dataKey="van" />
                                  <YAxis tickFormatter={v => `£${v}`} />
                                  <Tooltip formatter={v => `£${Number(v).toFixed(2)}`} contentStyle={{ background: '#1f1f1f', border: '1px solid #333', color: '#fff' }} itemStyle={{ color: '#fff' }} labelStyle={{ color: '#fff', fontWeight: 600 }} cursor={false} />
                                  <Legend />
                                  <Bar dataKey={(() => {
                                    if (dateFilter && typeof dateFilter.year === 'number' && typeof dateFilter.month === 'number' && dateFilter.month !== 'custom') {
                                      const selectedMonth = dayjs().year(dateFilter.year).month(dateFilter.month);
                                      return selectedMonth.subtract(2, 'month').format('YYYY-MM');
                                    } else if (dateFilter && dateFilter.dateRange && dateFilter.dateRange.length === 2) {
                                      const end = dateFilter.dateRange[1].endOf('month');
                                      const startMonth = end.subtract(2, 'month');
                                      return startMonth.format('YYYY-MM');
                                    } else {
                                      return dayjs().subtract(2, 'month').format('YYYY-MM');
                                    }
                                  })()} name={(() => {
                                    if (dateFilter && typeof dateFilter.year === 'number' && typeof dateFilter.month === 'number' && dateFilter.month !== 'custom') {
                                      const selectedMonth = dayjs().year(dateFilter.year).month(dateFilter.month);
                                      return selectedMonth.subtract(2, 'month').format('MMM YYYY');
                                    } else if (dateFilter && dateFilter.dateRange && dateFilter.dateRange.length === 2) {
                                      const end = dateFilter.dateRange[1].endOf('month');
                                      const startMonth = end.subtract(2, 'month');
                                      return startMonth.format('MMM YYYY');
                                    } else {
                                      return dayjs().subtract(2, 'month').format('MMM YYYY');
                                    }
                                  })()} fill="#FF6B6B" stackId="a" />
                                  <Bar dataKey={(() => {
                                    if (dateFilter && typeof dateFilter.year === 'number' && typeof dateFilter.month === 'number' && dateFilter.month !== 'custom') {
                                      const selectedMonth = dayjs().year(dateFilter.year).month(dateFilter.month);
                                      return selectedMonth.subtract(1, 'month').format('YYYY-MM');
                                    } else if (dateFilter && dateFilter.dateRange && dateFilter.dateRange.length === 2) {
                                      const end = dateFilter.dateRange[1].endOf('month');
                                      const startMonth = end.subtract(1, 'month');
                                      return startMonth.format('YYYY-MM');
                                    } else {
                                      return dayjs().subtract(1, 'month').format('YYYY-MM');
                                    }
                                  })()} name={(() => {
                                    if (dateFilter && typeof dateFilter.year === 'number' && typeof dateFilter.month === 'number' && dateFilter.month !== 'custom') {
                                      const selectedMonth = dayjs().year(dateFilter.year).month(dateFilter.month);
                                      return selectedMonth.subtract(1, 'month').format('MMM YYYY');
                                    } else if (dateFilter && dateFilter.dateRange && dateFilter.dateRange.length === 2) {
                                      const end = dateFilter.dateRange[1].endOf('month');
                                      const startMonth = end.subtract(1, 'month');
                                      return startMonth.format('MMM YYYY');
                                    } else {
                                      return dayjs().subtract(1, 'month').format('MMM YYYY');
                                    }
                                  })()} fill="#4ECDC4" stackId="a" />
                                  <Bar dataKey={(() => {
                                    if (dateFilter && typeof dateFilter.year === 'number' && typeof dateFilter.month === 'number' && dateFilter.month !== 'custom') {
                                      const selectedMonth = dayjs().year(dateFilter.year).month(dateFilter.month);
                                      return selectedMonth.format('YYYY-MM');
                                    } else if (dateFilter && dateFilter.dateRange && dateFilter.dateRange.length === 2) {
                                      const end = dateFilter.dateRange[1].endOf('month');
                                      return end.format('YYYY-MM');
                                    } else {
                                      return dayjs().format('YYYY-MM');
                                    }
                                  })()} name={(() => {
                                    if (dateFilter && typeof dateFilter.year === 'number' && typeof dateFilter.month === 'number' && dateFilter.month !== 'custom') {
                                      const selectedMonth = dayjs().year(dateFilter.year).month(dateFilter.month);
                                      return selectedMonth.format('MMM YYYY');
                                    } else if (dateFilter && dateFilter.dateRange && dateFilter.dateRange.length === 2) {
                                      const end = dateFilter.dateRange[1].endOf('month');
                                      return end.format('MMM YYYY');
                                    } else {
                                      return dayjs().format('MMM YYYY');
                                    }
                                  })()} fill="#45B7D1" stackId="a" />
                                </BarChart>
                              </ResponsiveContainer>
                            </div>
                        </Card>
                    </Col>
                  </Row>
                </Col>
              </Row>

              {loading && <div style={{textAlign: 'center', padding: '20px'}}><Spin tip="Processing data..."/></div>} 
            </>
          )}

          <Card
            title="Filtered Transaction List"
            extra={
              <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
                <Button type="primary" icon={<DownloadOutlined />}>Export List</Button>
              </Dropdown>
            }
          >
            <Table
              columns={columns}
              dataSource={transactions}
              loading={loading}
              rowKey={record => record._id}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} transactions in list`,
              }}
              scroll={{ x: 'max-content' }} 
            />
          </Card>
        </Space>
      </Content>
    </Layout>
  );
};

export default FinancialReport; 

